/**
 * Asetet e një punonjësi — paneli që jeton te profili (tabi "Asetet").
 *
 * Këtu shfaqet paralajmërimi i largimit. Sistemi nuk ka një ekran "largim"
 * ku të futej: `workers.active` ndryshohet nga listat, dhe nuk ka asnjë hap
 * ku dikush ndalet e lexon. Prandaj rri aty ku HR-ja shkon gjithsesi para se
 * të mbyllë një dosje — te profili — dhe thotë me shifra çka mbetet pa u
 * kthyer, jo një «kujdes» të përgjithshëm.
 *
 * I njëjti panel përdoret edhe si vetëshërbim (`self`): punonjësi sheh çka
 * mban, pa asnjë veprim mbi të.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Alert, Stack, CircularProgress, Chip,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import StatusChip from '../common/StatusChip';
import assetsService from '../../services/assetsService';
import { dateText, money, conditionLabel, conditionChip } from '../../config/assets';

export default function WorkerAssetsPanel({ workerId, self = false, title = 'Asetet' }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = self ? await assetsService.mine() : await assetsService.forWorker(workerId);
      setData(res);
    } catch (e) {
      // Një server i rënë dhe një punonjës pa pajisje NUK guxojnë të duken
      // njësoj — ndryshe "nuk ka asete" bëhet përgjigje e rreme te largimi.
      setError(e.message || 'Të dhënat e aseteve nuk u ngarkuan.');
    } finally {
      setLoading(false);
    }
  }, [workerId, self]);

  useEffect(() => {
    if (!self && !workerId) { setLoading(false); setData(null); return; }
    load();
  }, [load, self, workerId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Alert severity="info">Zgjidh një punonjës për të parë asetet e tij.</Alert>;

  const open    = data.open || [];
  const history = (data.assignments || []).filter((a) => !a.open);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: 'primary.soft', color: 'primary.main',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Inventory2Icon sx={{ fontSize: 19 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.2 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {open.length} në përdorim · {history.length} të kthyera
          </Typography>
        </Box>
      </Stack>

      {/* Paralajmërimi i largimit. Kur s'ka asgjë pa u kthyer, thuhet gjithsesi —
          një dosje e pastër duhet të lexohet si e pastër, jo si e paverifikuar. */}
      {open.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <strong>{data.clearance?.message}</strong>{' '}
          {self
            ? 'Kthimi regjistrohet nga HR-ja në momentin e dorëzimit.'
            : 'Regjistro kthimin para se të mbyllet marrëdhënia e punës.'}
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          {self ? 'Nuk ke asete të pakthyera.' : 'Punonjësi nuk ka asete të pakthyera.'}
        </Alert>
      )}

      {open.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Aseti</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Kodi</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Dorëzuar</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Gjendja në dorëzim</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Pajisjet shtesë</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {open.map((a) => (
                <TableRow key={a.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{a.assetName}</TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{a.assetCode}</TableCell>
                  <TableCell>{dateText(a.assignedOn)}</TableCell>
                  <TableCell>
                    <StatusChip status={conditionChip(a.condition)} label={conditionLabel(a.condition)} size="sm" />
                  </TableCell>
                  <TableCell>{a.accessories || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {history.length > 0 && (
        <>
          <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', mb: 0.75 }}>Historiku</Typography>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Aseti</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Periudha</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Kthyer në gjendje</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Dëmi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{a.assetName}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.assetCode}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateText(a.assignedOn)} → {dateText(a.returnedOn)}
                    </TableCell>
                    <TableCell>
                      {a.returnCondition
                        ? <StatusChip status={conditionChip(a.returnCondition)} label={conditionLabel(a.returnCondition)} size="sm" />
                        : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {a.damageCost ? money(a.damageCost) : '—'}
                      {a.deductionId ? <Chip size="small" label={`ndalesë #${a.deductionId}`} sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }} /> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
